var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AmortizationConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { DateConverter, JSON, JsonConverter, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { DateTime } from 'luxon';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
import { amortizationConfigSchemaValidation } from '../validations.generated';
export var AmortizationFrequency;
(function (AmortizationFrequency) {
    AmortizationFrequency["Daily"] = "days";
    AmortizationFrequency["Weekly"] = "weeks";
    AmortizationFrequency["Biweekly"] = "biweeks";
    AmortizationFrequency["Monthly"] = "months";
    AmortizationFrequency["Quarterly"] = "quarters";
    AmortizationFrequency["Yearly"] = "years";
})(AmortizationFrequency || (AmortizationFrequency = {}));
const MAX_RESULT_COLS = 100;
export const amortizationConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:AmortizationConfig',
    type: 'object',
    properties: {
        valueColumn: { type: 'string', minLength: 1 },
        startDateColumn: { type: 'string', minLength: 1 },
        endDateColumn: { type: 'string', minLength: 1 },
        minStartDate: { type: 'string', format: 'date-time' },
        maxEndDate: { type: 'string', format: 'date-time' },
        durationUnits: {
            type: 'string',
            enum: Object.keys(AmortizationFrequency).map((key) => AmortizationFrequency[key])
        },
        interestRate: { type: 'number', minimum: 0, maximum: 1 },
        indexColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        },
        isEven: { type: 'boolean' },
        maxResultColumns: { type: 'number', minimum: 0, maximum: 100 }
    },
    required: [
        'indexColumns',
        'valueColumn',
        'startDateColumn',
        'endDateColumn',
        'minStartDate',
        'maxEndDate',
        'durationUnits',
        'isEven',
        'maxResultColumns'
    ]
};
let AmortizationConfig = AmortizationConfig_1 = class AmortizationConfig extends ToolConfig {
    constructor(indexColumns, valueColumn, startDateColumn, endDateColumn, minStartDate, maxEndDate, durationUnits, isEven, interestRate) {
        super(ToolType.AMORTIZATION);
        this.indexColumns = indexColumns;
        this.valueColumn = valueColumn;
        this.startDateColumn = startDateColumn;
        this.endDateColumn = endDateColumn;
        this.minStartDate = minStartDate;
        this.maxEndDate = maxEndDate;
        this.durationUnits = durationUnits;
        this.isEven = isEven;
        this.maxResultColumns = MAX_RESULT_COLS;
        if (interestRate !== undefined)
            this.interestRate = interestRate;
        JSON.validate(this, AmortizationConfig_1);
    }
    static formatDateTime(dt, durationUnits) {
        // this helper function is used to format the column's names with the same naming convention used in the python/pyspark code.
        // Any changes here in terms of naming convention should be also reflected in the python/pyspark code.
        switch (durationUnits) {
            case 'days':
                return `${dt.year}-${dt.month.toString().padStart(2, '0')}-${dt.day.toString().padStart(2, '0')}`;
            case 'weeks':
                return `${dt.weekYear}-W${dt.weekNumber.toString().padStart(2, '0')}`;
            case 'biweeks':
                return `${dt.weekYear}-BW${Math.ceil(dt.weekNumber / 2)
                    .toString()
                    .padStart(2, '0')}`;
            case 'months':
                return `${dt.year}-M${dt.month.toString().padStart(2, '0')}`;
            case 'quarters':
                return `${dt.year}-Q${dt.quarter.toString().padStart(2, '0')}`;
            case 'years':
                return `${dt.year}Y`;
            default:
                throw new Error(`Unsupported amortization frequency: ${durationUnits}`);
        }
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return AmortizationConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return AmortizationConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return AmortizationConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return AmortizationConfig_1.outs;
    }
    static get in() {
        return AmortizationConfig_1.ins[0];
    }
    static get out() {
        return AmortizationConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.AMORTIZATION.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, [], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            const dependents = this.getGroupByDependents(exp, this.in, this.indexColumns); // getGroupByDependents also call exp.copy()
            const startDateColSource = exp.findSourceColumn(this.in, this.startDateColumn);
            const endDateColSource = exp.findSourceColumn(this.in, this.endDateColumn);
            const valueColSource = exp.findSourceColumn(this.in, this.valueColumn);
            dependents.push(startDateColSource, endDateColSource, valueColSource);
            exp.copy(this.in, startDateColSource, false, true);
            exp.copy(this.in, endDateColSource, false, true);
            exp.copy(this.in, valueColSource, false, true);
            // get the list of new date columns to be added to the output
            const newDateColumns = this.getNewDateOutputColumns();
            newDateColumns.forEach((col) => {
                exp.makeNew(new Column(col, 'number'), dependents, true);
            });
        }
        return tsb.build();
    }
    getNewDateOutputColumns() {
        // this function creates the list of new columns expected to be generated by the python/pyspark code
        // the datesDiff is first calculated in days in order to avoid logic errors if we just add time units such as weeks, months, etc
        // because of the concept of iso calendar (ISO 8601), between the interval of seven days, there is a chance to exist more than one iso week
        // if datesDiff just adds seven days to a specific startDate, the dateColumns  could miss an ISO week between the seven days interval
        // also calculating dateDiff in days ensures that the calculations on the python/pyspark and javascript code have the same results independent of the date libraries
        const startDate = DateTime.fromJSDate(this.minStartDate);
        const endDate = DateTime.fromJSDate(this.maxEndDate);
        const datesDiff = Math.ceil(endDate.diff(startDate, 'days').days);
        const maxDateColumns = this.maxResultColumns - (this.indexColumns ? this.indexColumns.length : 0) - 3;
        const dateColumns = new Set();
        for (let i = 0; i <= datesDiff && dateColumns.size < maxDateColumns; i++) {
            const dt = startDate.plus({ days: i });
            const formattedDt = AmortizationConfig_1.formatDateTime(dt, this.durationUnits);
            if (!dateColumns.has(formattedDt)) {
                dateColumns.add(formattedDt);
            }
        }
        return dateColumns;
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationState.BAD_METADATA;
        }
        const inputs = new Map([
            [this.valueColumn, ['number']],
            [this.startDateColumn, ['date']],
            [this.endDateColumn, ['date']]
        ]);
        this.addGroupByToInputs(inputs, this.indexColumns);
        // get the set of new date columns
        const newDateColumns = this.getNewDateOutputColumns();
        const outputs = this.indexColumns ? [...this.indexColumns, ...newDateColumns] : [...newDateColumns];
        outputs.push(this.valueColumn, this.startDateColumn, this.endDateColumn);
        if (!isSourceValid(source, inputs, [])) {
            return ToolValidationState.BAD_METADATA;
        }
        if (new Set(outputs).size !== outputs.length) {
            return ToolValidationState.BAD_METADATA;
        }
        return ToolValidationState.VALID;
    }
};
__decorate([
    JsonConverter(new DateConverter('date-time'))
], AmortizationConfig.prototype, "minStartDate", void 0);
__decorate([
    JsonConverter(new DateConverter('date-time'))
], AmortizationConfig.prototype, "maxEndDate", void 0);
AmortizationConfig = AmortizationConfig_1 = __decorate([
    JsonSchemaV2(amortizationConfigSchema, amortizationConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.AMORTIZATION)
], AmortizationConfig);
export { AmortizationConfig };
//# sourceMappingURL=amortizationConfig.js.map